/**
 * Button primitive - Direction A "Tu phap restraint"
 *
 * 4 variant: primary | secondary | ghost | danger
 * 3 size: sm (32px) | md (40px) | lg (48px)
 * State: default | hover | focus | disabled | loading
 *
 * A11y:
 *  - aria-label support (icon-only button)
 *  - aria-busy=true khi loading
 *  - aria-disabled=true khi disabled
 *  - keyboard accessible (native <button>)
 *  - focus ring: 2px gold offset 2px (global :focus-visible rule)
 *  - touch target >= 44x44px on mobile (md + lg)
 *
 * Token reuse only (NO hardcoded hex). See pages/src/styles/primitives.css.
 *
 * Phase 1 Track 2 (2026-05-22).
 */

import * as React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      type = 'button',
      className,
      children,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ) {
    const isDisabled = disabled || loading;
    const cls = [
      'ask-btn',
      `ask-btn--${variant}`,
      `ask-btn--${size}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type}
        className={cls}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        aria-label={ariaLabel}
        data-loading={loading || undefined}
        {...rest}
      >
        {children}
        {loading ? (
          <span className="ask-btn__spinner" aria-hidden="true">
            <span className="ask-btn__spinner-ring" />
          </span>
        ) : null}
      </button>
    );
  }
);
